import { ImagePlus, Save, Trash2, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api } from "../../api";
import type { AdminDashboard, AdminTvPhoto } from "../../types";
import { Dialog } from "../Dialog";

const maxFileSize = 12 * 1024 * 1024;
const maxInputPixels = 30_000_000;

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function validatePhoto(file: File) {
  const supportedType =
    file.type === "image/jpeg" ||
    file.type === "image/png" ||
    /\.(jpe?g|png)$/i.test(file.name);
  if (!supportedType) {
    throw new Error("Vyberte fotografii ve formátu JPG nebo PNG.");
  }
  if (file.size > maxFileSize) {
    throw new Error("Fotografie může mít nejvýše 12 MB.");
  }

  try {
    const bitmap = await createImageBitmap(file);
    const tooLarge = bitmap.width * bitmap.height > maxInputPixels;
    bitmap.close();
    if (tooLarge) {
      throw new Error(
        "Fotografie je příliš rozměrná. Exportujte ji nejvýše do 30 Mpx.",
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("30 Mpx")) {
      throw error;
    }
    throw new Error("Fotografii se nepodařilo přečíst jako JPG nebo PNG.");
  }
}

export function TvPhotosAdmin({
  dashboard,
  onChanged,
}: {
  dashboard: AdminDashboard;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<AdminTvPhoto | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminTvPhoto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const closeEditor = () => {
    if (!busy) setEditing(null);
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    const form = new FormData(event.currentTarget);
    const selected = form.get("photo");
    const file = selected instanceof File && selected.size ? selected : null;

    try {
      if (editing === "new" && !file) {
        throw new Error("Vyberte fotografii ve formátu JPG nebo PNG.");
      }
      if (file) await validatePhoto(file);

      const payload = new FormData();
      payload.set("caption", String(form.get("caption") || ""));
      payload.set("sortOrder", String(form.get("sortOrder") || "0"));
      if (file) payload.set("photo", file);

      await api(
        editing === "new"
          ? "/api/admin/tv-photos"
          : `/api/admin/tv-photos/${editing?.id}`,
        {
          method: editing === "new" ? "POST" : "PUT",
          csrfToken: dashboard.csrfToken,
          body: payload,
        },
      );
      setEditing(null);
      onChanged();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Fotografii nelze uložit.",
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/admin/tv-photos/${deleting.id}`, {
        method: "DELETE",
        csrfToken: dashboard.csrfToken,
      });
      setDeleting(null);
      onChanged();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Fotografii nelze smazat.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-content">
      <section className="admin-section">
        <header>
          <div>
            <h2>Fotografie v TV režimu</h2>
            <p>JPG a PNG se automaticky převedou na Full HD fotografii.</p>
          </div>
          <button
            className="button button--primary button--small"
            onClick={() => {
              setError("");
              setEditing("new");
            }}
          >
            <ImagePlus size={17} /> Přidat fotografii
          </button>
        </header>

        {dashboard.photos.length ? (
          <div className="admin-list admin-photo-list">
            {dashboard.photos.map((photo) => (
              <article className="admin-list__item" key={photo.id}>
                <div className="admin-photo-list__preview">
                  <img
                    src={photo.imageUrl}
                    alt={photo.caption || "Fotografie pro TV režim"}
                    loading="lazy"
                  />
                </div>
                <div className="admin-list__copy">
                  <strong>{photo.caption || "Bez popisku"}</strong>
                  <span>
                    {photo.width} × {photo.height} px · {formatBytes(photo.byteSize)}
                  </span>
                </div>
                <span className="admin-list__meta">Pořadí {photo.sortOrder}</span>
                <div className="admin-list__buttons">
                  <button
                    onClick={() => {
                      setError("");
                      setEditing(photo);
                    }}
                  >
                    Upravit
                  </button>
                  <button
                    className="admin-list__delete"
                    onClick={() => {
                      setError("");
                      setDeleting(photo);
                    }}
                    aria-label={`Smazat fotografii ${photo.caption || photo.id}`}
                    title="Smazat fotografii"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="admin-empty-state">
            <ImagePlus />
            <strong>Zatím tu není žádná fotografie.</strong>
            <span>Po nahrání se objeví ve střídání s TV vzkazy.</span>
          </div>
        )}
      </section>

      {editing && (
        <div className="admin-drawer">
          <header>
            <h3>{editing === "new" ? "Nová fotografie" : "Upravit fotografii"}</h3>
            <button
              className="icon-button"
              onClick={closeEditor}
              aria-label="Zavřít"
            >
              <X />
            </button>
          </header>
          <form className="form-stack" onSubmit={save}>
            {editing !== "new" && (
              <img
                className="admin-photo-editor__preview"
                src={editing.imageUrl}
                alt={editing.caption || "Upravovaná fotografie"}
              />
            )}
            <label>
              {editing === "new" ? "Fotografie" : "Nahradit fotografii"}
              <input
                name="photo"
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                required={editing === "new"}
              />
              <small>JPG nebo PNG · nejvýše 12 MB a 30 Mpx · výstup Full HD</small>
            </label>
            <label>
              Popisek (volitelný)
              <textarea
                name="caption"
                maxLength={300}
                rows={4}
                defaultValue={editing === "new" ? "" : editing.caption}
              />
            </label>
            <label>
              Pořadí
              <input
                name="sortOrder"
                type="number"
                min="0"
                max="1000"
                required
                defaultValue={
                  editing === "new"
                    ? Math.max(
                        -1,
                        ...dashboard.photos.map((photo) => photo.sortOrder),
                      ) + 1
                    : editing.sortOrder
                }
              />
            </label>
            {error && <p className="form-error">{error}</p>}
            <button className="button button--primary" disabled={busy}>
              <Save size={17} /> {busy ? "Ukládám…" : "Uložit"}
            </button>
          </form>
        </div>
      )}

      <Dialog
        open={Boolean(deleting)}
        title="Smazat fotografii?"
        onClose={() => {
          if (!busy) setDeleting(null);
        }}
      >
        <div className="admin-delete-confirmation">
          <p>
            Fotografie <strong>{deleting?.caption || "bez popisku"}</strong> bude
            trvale odstraněna z administrace i TV režimu.
          </p>
          {error && <p className="form-error">{error}</p>}
          <div className="admin-delete-confirmation__actions">
            <button
              className="button button--outline"
              disabled={busy}
              onClick={() => setDeleting(null)}
            >
              Ponechat
            </button>
            <button
              className="button button--danger"
              disabled={busy}
              onClick={() => void remove()}
            >
              <Trash2 size={17} /> {busy ? "Mažu…" : "Smazat fotografii"}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
